import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { Shield } from "lucide-react";

export default function Privacy() {
    return (
        <div className="min-h-screen bg-gradient-subtle flex flex-col">
            <SEO
                title="Privacy Policy - TranslateLoo AI"
                description="TranslateLoo AI Privacy Policy - Learn how we protect your data"
            />
            <Header />

            <main className="flex-1 max-w-4xl mx-auto px-4 py-12 sm:py-16">
                <article className="space-y-8">
                    {/* Header */}
                    <header className="text-center space-y-4 mb-12">
                        <div className="inline-flex p-3 bg-gradient-primary rounded-2xl mb-4">
                            <Shield className="h-10 w-10 text-white" />
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                            Privacy Policy
                        </h1>
                        <p className="text-muted-foreground">
                            Last updated: February 2026
                        </p>
                    </header>

                    {/* Content */}
                    <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
                        <Section title="1. Introduction">
                            <p>
                                TranslateLoo AI ("we", "our", or "us") is committed to protecting your privacy.
                                This Privacy Policy explains how we collect, use, and safeguard your information
                                when you use our translation service at translateloo.com.
                            </p>
                        </Section>

                        <Section title="2. Information We Collect">
                            <p>We collect minimal information to provide our service:</p>
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                                <li>
                                    <strong>Translation Content:</strong> Text you submit for translation is sent to
                                    our servers for processing. This data is not permanently stored.
                                </li>
                                <li>
                                    <strong>Local Storage:</strong> Translation history is stored locally in your
                                    browser. We do not have access to this data.
                                </li>
                                <li>
                                    <strong>Usage Analytics:</strong> We may collect anonymous usage statistics to
                                    improve our service (page views, feature usage).
                                </li>
                            </ul>
                        </Section>

                        <Section title="3. How We Use Your Information">
                            <p>Your information is used to:</p>
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                                <li>Process and deliver translation results</li>
                                <li>Improve our translation accuracy and features</li>
                                <li>Maintain and optimize our service</li>
                                <li>Respond to support requests</li>
                            </ul>
                        </Section>

                        <Section title="4. Data Retention">
                            <p>
                                Translation requests are processed in real-time and are not stored on our servers
                                beyond the time needed to complete the translation. Your translation history is
                                stored locally in your browser and remains under your control. You can delete it
                                at any time through the application interface.
                            </p>
                        </Section>

                        <Section title="5. Third-Party Services">
                            <p>We use the following third-party services:</p>
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                                <li>
                                    <strong>Supabase:</strong> For secure API hosting and edge functions
                                </li>
                                <li>
                                    <strong>n8n:</strong> For workflow automation and translation processing
                                </li>
                            </ul>
                            <p>
                                These services have their own privacy policies and security measures. We ensure
                                all third-party providers meet our security and privacy standards.
                            </p>
                        </Section>

                        <Section title="6. Cookies">
                            <p>
                                We use essential cookies to maintain your preferences (such as dark mode settings).
                                We do not use tracking cookies or third-party advertising cookies.
                            </p>
                        </Section>

                        <Section title="7. Your Rights">
                            <p>You have the right to:</p>
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                                <li>Clear your local translation history at any time</li>
                                <li>Request information about data we may have processed</li>
                                <li>Contact us with privacy-related concerns</li>
                            </ul>
                        </Section>

                        <Section title="8. Security">
                            <p>
                                We implement industry-standard security measures including HTTPS encryption,
                                secure API endpoints, and regular security reviews to protect your data during
                                transmission and processing.
                            </p>
                        </Section>

                        <Section title="9. Children's Privacy">
                            <p>
                                Our service is not directed to children under 13. We do not knowingly collect
                                personal information from children under 13. If you believe we have collected
                                such information, please contact us.
                            </p>
                        </Section>

                        <Section title="10. Changes to This Policy">
                            <p>
                                We may update this Privacy Policy from time to time. We will notify users of
                                significant changes by posting the new policy on this page with an updated
                                revision date.
                            </p>
                        </Section>

                        <Section title="11. Contact Us">
                            <p>
                                If you have questions about this Privacy Policy, please contact us at:{" "}
                                <a
                                    href="mailto:privacy@translateloo.com"
                                    className="text-primary hover:underline"
                                >
                                    privacy@translateloo.com
                                </a>
                            </p>
                        </Section>
                    </div>
                </article>
            </main>

            <Footer />
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="bg-translator-panel/30 rounded-xl p-6 border border-translator-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{title}</h2>
            <div className="text-muted-foreground space-y-3">{children}</div>
        </section>
    );
}
